import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VFC } from 'react';

import { BannerStyle } from '../../../../typings/enums';
import * as AsyncValue from '../../../redux/asyncValue';
import { clearLookupError } from '../../../redux/globalLicenseCorrections/actions';
import { selectors } from '../../../redux/globalLicenseCorrections/slice';
import Accordion from '../../generic/Accordion';
import Banner from '../../generic/Banner';
import Card from '../../generic/Card/Card';
import Loader from '../../generic/Loader';
import { LoadingBoundary } from '../../generic/LoadingBoundary';
import Text from '../../generic/Text';

import { BottomActions } from './BottomActions';
import { LicenseEditor } from './LicenseEditor';
import { ProjectInfo } from './ProjectInfo';
import { ProjectSelector } from './ProjectSelector';

type Props = {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

/**
 * A card that allows admins to do global license corrections.
 */
export const LicenseCorrectionCard: VFC<Props> = ({ isOpen, onToggle }) => {
  const [isSectionOpen, setIsSectionOpen] = useState(isOpen ?? false);
  const handleToggle = () => {
    setIsSectionOpen(!isSectionOpen);
    onToggle?.(isSectionOpen);
  };

  const dispatch = useDispatch();
  const projectValue = useSelector(selectors.projectSelector);
  const revisionValue = useSelector(selectors.revisionSelector);

  const onDismissError = useCallback(() => {
    dispatch(clearLookupError());
  }, [dispatch]);

  // Use `as const` to force the compiler to treat this as a tuple.
  const values = AsyncValue.all([projectValue, revisionValue] as const);

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="global-license-corrections" isInitiallyOpen={isSectionOpen}>
          <Accordion.Target onClick={handleToggle}>Global License Corrections</Accordion.Target>
          <Accordion.Body>
            <Text.H4>Update Licenses</Text.H4>
            <ProjectSelector />
            <LoadingBoundary
              asyncValue={values}
              renderValue={({ value: [project, revision] }) =>
                <div data-testid="project-and-license-editor">
                  <ProjectInfo project={project} revision={revision} />
                  <LicenseEditor revisionLocator={revision.locator} />
                  <BottomActions dependencyRevisionLocator={revision.locator} />
                </div>}
              renderEmpty={
                <ul>
                  <li>Enter the locator above for the component you wish to edit.</li>
                  <li>This will affect the ground-truth data for all organizations.</li>
                  <li>The most recent revision will be shown if you do not provide a revision.</li>
                  <li>This tool does not currently support license groups with more than one license.</li>
                  <li>The tool does not currently limit changes to a subset of revisions.</li>
                  <li>Changes will take affect on all future analyses.</li>
                </ul>
            }
              renderError={({ errors }) =>
                <Banner
                  bannerStyle={BannerStyle.High}
                  title={errors.join('. ')}
                  body=""
                  isDismissible
                  onDismiss={onDismissError}
                />}
              renderLoading={<Loader />}
            />
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default LicenseCorrectionCard;
